import { useRef, useState, type ReactNode } from 'react';
import { Disclosure } from './Disclosure';
import { cn } from './cn';

export interface AccordionItem {
  id: string;
  title: ReactNode;
  description?: string;
  content: ReactNode;
  disabled?: boolean;
}
interface AccordionProps {
  items: readonly AccordionItem[];
  type?: 'single' | 'multiple';
  value?: readonly string[];
  defaultValue?: readonly string[];
  onValueChange?: (value: string[]) => void;
  headingLevel?: 2 | 3 | 4 | 5 | 6;
  className?: string;
}
export function Accordion({
  items,
  type = 'single',
  value,
  defaultValue = [],
  onValueChange,
  headingLevel = 3,
  className,
}: AccordionProps) {
  const [local, setLocal] = useState(defaultValue);
  const root = useRef<HTMLDivElement>(null);
  const selected = (value ?? local).filter((id) => items.some((item) => item.id === id));
  const visible = type === 'single' ? selected.slice(0, 1) : selected;
  return (
    <div
      ref={root}
      data-slot="accordion"
      data-type={type}
      className={cn(
        'min-w-0 divide-y divide-line overflow-hidden rounded-lg border border-line bg-card',
        className,
      )}
      onKeyDown={(event) => {
        if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return;
        const target = event.target;
        if (
          !(target instanceof HTMLButtonElement) ||
          !target.hasAttribute('data-disclosure-trigger') ||
          target.closest('[data-slot="disclosure"]')?.parentElement !== root.current
        )
          return;
        const triggers = [
          ...(root.current?.querySelectorAll<HTMLButtonElement>('[data-disclosure-trigger]:not(:disabled)') ??
            []),
        ].filter((button) => button.closest('[data-slot="disclosure"]')?.parentElement === root.current);
        const index = triggers.indexOf(target);
        if (index < 0 || !triggers.length) return;
        const next =
          event.key === 'Home'
            ? 0
            : event.key === 'End'
              ? triggers.length - 1
              : event.key === 'ArrowDown'
                ? (index + 1) % triggers.length
                : event.key === 'ArrowUp'
                  ? (index - 1 + triggers.length) % triggers.length
                  : null;
        if (next !== null) {
          event.preventDefault();
          triggers[next].focus();
        }
      }}
    >
      {items.map((item) => (
        <Disclosure
          key={item.id}
          title={item.title}
          description={item.description}
          disabled={item.disabled}
          variant="row"
          region={false}
          headingLevel={headingLevel}
          open={visible.includes(item.id)}
          onOpenChange={(open) => {
            const next = open
              ? type === 'single'
                ? [item.id]
                : [...visible, item.id]
              : visible.filter((id) => id !== item.id);
            if (value === undefined) setLocal(next);
            onValueChange?.(next);
          }}
        >
          {item.content}
        </Disclosure>
      ))}
    </div>
  );
}
